import { AbstractShape } from '../abstract/Shape';
import { Connection } from '../clases/Connection';
import { LiveCreatorInvocationContext } from '../interfaces/live-creator.contract';

export function flattenShapes(roots: readonly AbstractShape[]): AbstractShape[] {
  const seen = new Set<AbstractShape>();
  const result: AbstractShape[] = [];
  const visit = (shape: AbstractShape) => {
    if (seen.has(shape)) return;
    seen.add(shape);
    result.push(shape);
    shape.children.forEach(visit);
  };
  roots.forEach(visit);
  return result;
}

export function canvasSnapshot(roots: readonly AbstractShape[], connections: readonly Connection[]): LiveCreatorInvocationContext['canvas'] {
  const shapes = flattenShapes(roots);
  const ids = new Set(shapes.map(item => item.id));
  // Imported FLOW endpoints can temporarily be lightweight renderer handles
  // whose own dbPropierties are not hydrated yet. The represented shape list
  // is already authoritative, so resolve canonical endpoint UUIDs by Canvas id
  // instead of silently dropping them from the agent protocol.
  const dbIdByCanvasId = new Map(shapes
    .filter(item => typeof item.dbPropierties?.__EDT_UUID__ === 'string')
    .map(item => [item.id, item.dbPropierties.__EDT_UUID__]));
  return {
    selection: null, viewport: null,
    elements: shapes.filter(item => typeof item.dbPropierties?.__EDT_UUID__ === 'string').map(item => ({
      id: item.id, dbId: item.dbPropierties.__EDT_UUID__, name: item.name,
      parentId: item.parentId ?? null, parentDbId: item.parent?.dbPropierties?.__EDT_UUID__ ?? null,
      x: item.x, y: item.y, width: item.width, height: item.height,
      shape: item.shape, color: item.theme_color.name, textWidth: item.textWidth,
      dottedSpacing: item.dottedSpacing, childrenCount: item.children.length
    })),
    // `iFrom`/`iTo` are renderer-effective shapes and can point at a visible
    // container after findFilled(). `from`/`to` retain the actual canonical
    // FLOW endpoint instances and are therefore the protocol authority.
    connections: connections.filter(item => ids.has(item.from) && ids.has(item.to)).map(item => ({
      relation: item.dbPropierties?.__EDT_UUID__ ?? item.id,
      from: item.from, to: item.to,
      sourceDbId: dbIdByCanvasId.get(item.from) ?? item.iFrom.dbPropierties?.__EDT_UUID__,
      targetDbId: dbIdByCanvasId.get(item.to) ?? item.iTo?.dbPropierties?.__EDT_UUID__,
      lineColor: item.lineColor,
      type: item.type, arrowStyle: item.arrowStyle, arrowType: item.arrowType,
      anchorFrom: item.anchorFrom, anchorTo: item.anchorTo, textPositionPercent: item.textPositionPercent,
      lArrowHorizontalSpacing: item.lArrowHorizontalSpacing, lArrowVerticalSpacing: item.lArrowVerticalSpacing
    }))
  };
}
